import React, { BaseSyntheticEvent, useRef, useState } from 'react';
import { useMutate } from 'restful-react';
import { Dropdown } from '../../FormComponents/Dropdown/Dropdown';
import { TextInput } from '../../FormComponents/TextInput/TextInput';
import { BaseModal } from '../BaseModal/BaseModal';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import './ProjectModal.scss';
import { Button } from '../../FormComponents/Buttons/Button';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import { ValidationError } from '../../FormComponents/ValidationError/ValidationError';
import { durationInMinutes, timezones } from '../../../common/constants';
import history from '../../../tools/history';
import {
  formatDate,
  getBaseUtcOffset,
  getTimezoneName,
  isDaylightSavings,
  isValidTime,
  isValidTimeZone,
  mergeDateAndTime,
} from '../../../helpers/dateFormatHelper/dateFormatHelper';
import ErrorMessage from '../../ErrorMessage/ErrorMessage';
import { useTranslation } from 'react-i18next';
import { Project } from '../../../types/Project';
import { errorMessageToTranslationKey } from '../../../helpers/errorMessageHelper/errorMessageHelper';
import moment from 'moment';
import LoadingSpinner from '../../LoadingSpinner/LoadingSpinner';
// import AutoStartInput from '../../InputComponents/AutoStartInput/AutoStartInput';

type Props = {
  isOpen: boolean;
  contentLabel: string;
  project?: Project | null;
  handleCloseModal: () => void;
};

type CreateProjectFormData = {
  id: string;
  name: string;
  scheduledTimeZone: string;
  scheduledDateTime: Date | string;
  scheduledTime: string;
  scheduledDuration: string;
  autoStart: boolean;
};

export const ProjectModal = (props: Props) => {
  const date = props.project?.scheduledDateTime || new Date();
  const time = props.project?.scheduledTime || '';

  const defaultValues: CreateProjectFormData = {
    id: props.project?.id || '',
    name: props.project?.name || '',
    scheduledDateTime: date,
    scheduledTime: time,
    scheduledTimeZone: getTimezoneName(
      date,
      time,
      props.project?.scheduledTimeZone || 'GMT Standard Time',
    ),
    scheduledDuration: props.project?.scheduledDuration.toString() || '60',
    autoStart: props.project?.stream?.autoStart || false,
  };

  const { t } = useTranslation();
  const { control, errors, handleSubmit, register } = useForm<CreateProjectFormData>({
    defaultValues,
  });
  const [loadingCreate, setLoadingCreate] = useState(false);
  const timezoneRef = useRef(defaultValues.scheduledTimeZone);
  const dateRef = useRef(defaultValues.scheduledDateTime);
  const timeRef = useRef(defaultValues.scheduledTime);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onSuccessfulCreate = (body: CreateProjectFormData, data: any) => {
    if (data.statusCode === 200) {
      props.handleCloseModal();
      history.push('/producer/' + data.result);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onSuccessfulUpdate = (body: CreateProjectFormData, data: any) => {
    if (data.statusCode === 200) {
      props.handleCloseModal();
    }
  };

  const { mutate: createProject, error: errorCreate } = useMutate<CreateProjectFormData>({
    verb: 'POST',
    path: '/projects',
    onMutate: onSuccessfulCreate,
  });

  const {
    mutate: updateProject,
    loading: loadingUpdate,
    error: errorUpdate,
  } = useMutate<CreateProjectFormData>({
    verb: 'PUT',
    path: '/projects',
    onMutate: onSuccessfulUpdate,
  });

  const onSubmit: SubmitHandler<CreateProjectFormData> = (
    data: CreateProjectFormData,
    event?: BaseSyntheticEvent,
  ) => {
    setLoadingCreate(true);
    event?.preventDefault();
    data.scheduledDateTime = moment(data.scheduledDateTime).format('YYYY-MM-DD');
    data.scheduledTime = timeRef.current;

    return props.project?.id ? updateProject(data) : createProject(data);
  };

  const getDate = (): string =>
    mergeDateAndTime(formatDate(moment(dateRef.current).toDate()), timeRef.current);

  const parseOffset = (offsetStr: string) => {
    const [hours, minutes, seconds] = offsetStr.split(':').map(Number);
    return hours + minutes / 60 + seconds / 3600;
  };
  
  const getTimezones = () => {  
    const summerSet = new Set(
      timezones
        .filter(tz => tz.isDaylightSavings)
        .map(tz => tz.value.replace(' Summer Time', ' Standard Time'))
    );

    const isCurrentlyDaylightSavings = isDaylightSavings(getDate(), getBaseUtcOffset('GMT Standard Time'));
  
    return timezones
      .filter(tz =>
        !isCurrentlyDaylightSavings
          ? !tz.isDaylightSavings
          : tz.isDaylightSavings || !summerSet.has(tz.value)
      )
      .sort((a, b) => parseOffset(a.baseUtcOffset) - parseOffset(b.baseUtcOffset));
  };  

  const changeTimezone = (): void => {
    const date = getDate();

    if (
      isDaylightSavings(date, getBaseUtcOffset(getTimezoneName(date, time, 'GMT Standard Time')))
    ) {
      timezoneRef.current = timezoneRef.current.replace('Standard', 'Summer');
    } else {
      timezoneRef.current = timezoneRef.current.replace('Summer', 'Standard');
    }

    control.setValue('scheduledTimeZone', '');
    control.setValue('scheduledTimeZone', timezoneRef.current);
  };

  if (errorCreate) {
    return <ErrorMessage message={t(errorMessageToTranslationKey(errorCreate))} />;
  }

  if (errorUpdate) {
    return <ErrorMessage message={t(errorMessageToTranslationKey(errorUpdate))} />;
  }

  return (
    <BaseModal
      isOpen={props.isOpen}
      contentLabel={props.contentLabel}
      handleCloseModal={props.handleCloseModal}
      ariaHideApp={true}
      title={'create-project.modal-title'}
    >
      <form className="create-project-form" onSubmit={handleSubmit(onSubmit)}>
        <input
          name="id"
          defaultValue={defaultValues.id}
          type="hidden"
          ref={() => register({ name: 'id' })}
        />
        <Controller
          name="name"
          control={control}
          rules={{ required: true, maxLength: 150 }}
          render={({ onChange, value }) => (
            <TextInput
              placeholder="create-project.project-title-placeholder"
              value={value}
              onChange={onChange}
              onAfterChange={null}
              errorType={errors.name}
              errorMessageKey={
                value.length <= 150
                  ? 'create-project.missing-title'
                  : 'create-project.title-max-length'
              }
            />
          )}
        />
        <div className="columns">
          <div className="column">
            <ValidationError
              errorType={errors.scheduledDateTime}
              errorMessageKey="create-project.missing-date"
            />
            <Controller
              name="scheduledDateTime"
              control={control}
              rules={{ required: true }}
              render={({ onChange, value }) => (
                <Calendar
                  onChange={(data) => {
                    dateRef.current = formatDate(data as Date);
                    onChange(data);
                    changeTimezone();
                  }}
                  minDate={new Date()}
                  minDetail="year"
                  next2Label={null}
                  prev2Label={null}
                  value={value ? new Date(value) : new Date()}
                />
              )}
            />
          </div>
          <div className="column">
            <ValidationError
              errorType={errors.scheduledTime}
              errorMessageKey={errors.scheduledTime?.message ?? ''}
            />
            <Controller
              name="scheduledTime"
              control={control}
              rules={{
                required: 'create-project.missing-time',
                validate: (value) => isValidTime(value, getDate(), timezoneRef.current),
              }}
              render={({ onChange, value }) => (
                <input
                  type="time"
                  onChange={(data) => {
                    timeRef.current = data.currentTarget.value;
                    onChange(data);
                    changeTimezone();
                  }}
                  className={'input timepicker' + (errors.scheduledTime ? ' is-danger' : '')}
                  id="timepicker"
                  aria-label="start time"
                  name="timepicker"
                  value={value}
                ></input>
              )}
            />
            <Controller
              control={control}
              name="scheduledTimeZone"
              className="mb-3"
              rules={{ required: true, validate: (value) => isValidTimeZone(value) }}
              render={({ onChange, value }) => (
                <Dropdown
                  value={value || getTimezoneName(getDate(), timeRef.current, value)}
                  options={getTimezones()}
                  onChange={(data) => {
                    timezoneRef.current = data?.value;
                    onChange(data?.value);
                  }}
                  placeholderKey="create-project.timezone-placeholder"
                  errorType={errors.scheduledTimeZone}
                  errorMessageKey="create-project.missing-timezone"
                />
              )}
            />
            <Controller
              control={control}
              name="scheduledDuration"
              className="mb-3"
              rules={{ required: true, min: durationInMinutes[0].value }}
              render={({ onChange, value }) => (
                <Dropdown
                  value={value || '60'}
                  options={durationInMinutes}
                  onChange={(data) => onChange(data?.value)}
                  placeholderKey="create-project.duration-placeholder"
                  errorType={errors.scheduledDuration}
                  errorMessageKey="create-project.missing-duration"
                />
              )}
            />
            {/* <Controller
              control={control}
              name="autoStart"
              className="mb-3"
              rules={{ required: false }}
              render={({ onChange, value }) => (
                <AutoStartInput
                  value={value}
                  onChange={(data, event) => {
                    onChange(data);
                    event?.preventDefault();
                  }} />
              )}
            /> */}
          </div>
        </div>
        <div className="shutoff-notice">
          <p>{t('create-project.auto-shutoff-1')}</p>
        </div>
        {props.project?.id ? (
          <>
            {!loadingUpdate ? (
              <Button
                translationKey="create-project.update"
                variant="primary"
                color="blue"
                onClick={() => null}
                type="submit"
                disabled={loadingUpdate}
              />
            ) : (
              <LoadingSpinner />
            )}
          </>
        ) : (
          <>
            {!loadingCreate ? (
              <Button
                translationKey="create-project.submit"
                variant="primary"
                color="blue"
                onClick={() => null}
                type="submit"
                disabled={loadingCreate}
              />
            ) : (
              <LoadingSpinner />
            )}
          </>
        )}
      </form>
    </BaseModal>
  );
};
